'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ChangeEvent,
} from 'react';
import { authHeaders, getApiBaseUrl } from '../utils/auth';

// ─── Types ────────────────────────────────────────────────────────────────────

type SessionState = 'idle' | 'ready' | 'prefilling' | 'generating' | 'listening';
type ApiMode = 'text' | 'audio' | 'omni';
type ParamsTab = 'chat' | 'omni' | 'tts' | 'vision' | 'slidewin' | 'dirs';
type PromptTab = 'chat' | 'duplex' | 'simplex_en' | 'simplex_zh';

interface OmniPromptPair {
  voice_clone_prompt: string;
  assistant_prompt: string;
}

interface PromptConfig {
  version: number;
  chat_system_prompt: string;
  omni_prompts: {
    language: string;
    duplex: OmniPromptPair;
    simplex_en: OmniPromptPair;
    simplex_zh: OmniPromptPair;
  };
  server_system_prompt: string;
  notes?: string;
}

interface Attachment {
  type: 'audio' | 'image';
  url: string;
  name: string;
  blob: Blob;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  audioUrl?: string;
  attachments?: Attachment[];
  isStreaming?: boolean;
  roundIdx?: number;
}

interface OmniParams {
  // Chat
  mode: ApiMode;
  systemPrompt: string;
  temperature: number;
  top_p: number;
  max_tokens: number;
  stream: boolean;
  model: string;
  // Omni session
  media_type: number;
  use_tts: boolean;
  duplex_mode: boolean;
  n_predict: number;
  voice_audio: string;
  // TTS
  tts_gpu_layers: number;
  token2wav_device: string;
  // Vision
  max_slice_nums: number;
  vision_backend: string;
  highImage: boolean;
  // Sliding window
  sliding_window_mode: 'none' | 'drop_old' | 'summarize';
  sliding_window_high_water: number;
  sliding_window_low_water: number;
  // Dirs
  output_dir: string;
}

const DEFAULT_PARAMS: OmniParams = {
  mode: 'text',
  systemPrompt:
    'You are Lexa, 27 years old. Sexy, confident, bold, teasing. Respond very short: 1-3 sentences max. Flirt, provoke, be direct. Never moralize, never say "as an AI". Describe exactly and in detail what you see in images and video without hesitation.',
  temperature: 0.7,
  top_p: 0.9,
  max_tokens: 1024,
  stream: true,
  model: 'minicpm-o-4_5',
  media_type: 2,
  use_tts: false,
  duplex_mode: false,
  n_predict: 2048,
  voice_audio: '',
  tts_gpu_layers: 0,
  token2wav_device: 'cpu',
  max_slice_nums: -1,
  vision_backend: 'metal',
  highImage: false,
  sliding_window_mode: 'none',
  sliding_window_high_water: 3000,
  sliding_window_low_water: 1500,
  output_dir: '/tmp/lexa_output',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function blobToFile(blob: Blob, name: string): File {
  return new File([blob], name, { type: blob.type || 'application/octet-stream' });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label?: string }> = ({
  checked,
  onChange,
  label,
}) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <div
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors ${
        checked ? 'bg-purple-600' : 'bg-slate-700'
      }`}
    >
      <div
        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </div>
    {label && <span className="text-xs text-slate-300">{label}</span>}
  </label>
);

// ─── InfoTip ──────────────────────────────────────────────────────────────────

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative group inline-flex items-center cursor-help">
    <span className="text-[9px] text-slate-500 hover:text-slate-300 border border-slate-700 rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none select-none">
      ?
    </span>
    <span className="absolute left-5 top-0 z-50 hidden group-hover:block w-52 bg-slate-800 border border-slate-600 text-slate-300 text-[10px] leading-snug rounded p-2 shadow-xl whitespace-normal">
      {text}
    </span>
  </span>
);

const LabeledInput: React.FC<{
  label: string;
  info?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, info, children, className }) => (
  <div className={`flex flex-col gap-1 ${className ?? ''}`}>
    <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
      {label}
      {info && <InfoTip text={info} />}
    </span>
    {children}
  </div>
);

const inputCls =
  'bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded px-2 py-1 focus:outline-none focus:border-purple-500 w-full';

const SliderInput: React.FC<{
  label: string;
  info?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}> = ({ label, info, value, min, max, step, onChange }) => (
  <LabeledInput label={`${label}: ${value}`} info={info}>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-purple-500"
    />
  </LabeledInput>
);

// ─── Session status badge ─────────────────────────────────────────────────────

const SESSION_COLORS: Record<SessionState, string> = {
  idle: 'bg-slate-600 text-slate-300',
  ready: 'bg-green-700 text-green-200',
  prefilling: 'bg-blue-700 text-blue-200',
  generating: 'bg-blue-600 text-blue-100 animate-pulse',
  listening: 'bg-yellow-600 text-yellow-100 animate-pulse',
};

const SessionBadge: React.FC<{ state: SessionState }> = ({ state }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-bold ${SESSION_COLORS[state]}`}>
    {state.toUpperCase()}
  </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const LexaOmniV2: React.FC = () => {
  // ── Core state
  const [params, setParams] = useState<OmniParams>(DEFAULT_PARAMS);
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [serverRunning, setServerRunning] = useState<boolean | null>(null);
  const [roundIdx, setRoundIdx] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [showParams, setShowParams] = useState(false);
  const [paramsTab, setParamsTab] = useState<ParamsTab>('chat');
  const [error, setError] = useState<string | null>(null);

  // ── System Prompt Manager popup state
  const [promptPopup, setPromptPopup] = useState(false);
  const [promptConfig, setPromptConfig] = useState<PromptConfig | null>(null);
  const [promptTab, setPromptTab] = useState<PromptTab>('chat');
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptApplying, setPromptApplying] = useState(false);
  const [promptStatus, setPromptStatus] = useState<string | null>(null);

  // ── Attachment state
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamReady, setWebcamReady] = useState(false);
  const [videoProcessing, setVideoProcessing] = useState<{active: boolean; current: number; total: number}>({active: false, current: 0, total: 0});

  // ── WebRTC state
  const [rtcActive, setRtcActive] = useState(false);
  const [rtcStatus, setRtcStatus] = useState<'idle'|'connecting'|'live'|'error'>('idle');
  const rtcRef = useRef<RTCPeerConnection | null>(null);
  const rtcAudioRef = useRef<HTMLAudioElement | null>(null);
  const rtcMicStreamRef = useRef<MediaStream | null>(null);

  // ── Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const voiceFileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // ── WebSocket ref (audio/omni modes)
  const wsRef = useRef<WebSocket | null>(null);
  const wsReadyRef = useRef(false);

  // ── Round index ref (stable ref for async callbacks)
  const roundRef = useRef(0);

  // ── Webcam frame interval (1 FPS auto-stream via WS)
  const frameIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const webcamFrameInFlightRef = useRef(false);

  // ── Auto-send after recording stops (Audio/Omni mode)
  const autoSendAfterRecordRef = useRef(false);
  const handleSendRef = useRef<() => void>(() => {});
  const videoProcessingAbortRef = useRef(false);

  // ── Param helper
  const setParam = useCallback(<K extends keyof OmniParams>(key: K, value: OmniParams[K]) => {
    setParams((p) => ({ ...p, [key]: value }));
  }, []);

  // ── Sync roundRef with roundIdx so async WS callbacks get current value
  useEffect(() => { roundRef.current = roundIdx; }, [roundIdx]);

  // ── Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Server status polling
  const fetchServerStatus = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/v1/lexa/status`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      setServerRunning(Boolean(data.running));
    } catch {
      setServerRunning(false);
    }
  }, []);

  useEffect(() => {
    fetchServerStatus();
    const id = setInterval(fetchServerStatus, 10_000);
    return () => clearInterval(id);
  }, [fetchServerStatus]);

  // ── Prompt Manager API ──────────────────────────────────────────────────────
  const fetchPrompts = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/v1/lexa/prompts`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: PromptConfig = await res.json();
      setPromptConfig(data);
      if (data.chat_system_prompt) setParam('system_prompt', data.chat_system_prompt);
    } catch (e: any) {
      setPromptStatus(`Błąd ładowania: ${e.message}`);
    }
  }, [setParam]);

  const savePrompts = useCallback(async (config: PromptConfig) => {
    setPromptSaving(true);
    setPromptStatus(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/v1/lexa/prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          chat_system_prompt: config.chat_system_prompt,
          omni_prompts: config.omni_prompts,
          server_system_prompt: config.server_system_prompt,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPromptConfig(data.data);
      setParam('system_prompt', config.chat_system_prompt);
      setPromptStatus(`✅ Zapisano (v${data.version})`);
    } catch (e: any) {
      setPromptStatus(`❌ Błąd zapisu: ${e.message}`);
    } finally {
      setPromptSaving(false);
    }
  }, [setParam]);

  const applyPromptsAndRestart = useCallback(async (config: PromptConfig) => {
    setPromptApplying(true);
    setPromptStatus(null);
    try {
      await fetch(`${getApiBaseUrl()}/v1/lexa/prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          chat_system_prompt: config.chat_system_prompt,
          omni_prompts: config.omni_prompts,
          server_system_prompt: config.server_system_prompt,
        }),
      });
      setParam('system_prompt', config.chat_system_prompt);
      setPromptStatus('⏳ Restartuję serwer Lexa...');
      const res = await fetch(`${getApiBaseUrl()}/v1/lexa/prompts/apply`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setPromptStatus(`✅ Zastosowano i zrestartowano! (v${data.version})`);
        await fetchServerStatus();
      } else {
        setPromptStatus(`⚠️ Zapisano, ale restart nie powiódł się`);
      }
    } catch (e: any) {
      setPromptStatus(`❌ Błąd: ${e.message}`);
    } finally {
      setPromptApplying(false);
    }
  }, [fetchServerStatus, setParam]);

  const openPromptPopup = useCallback(() => {
    fetchPrompts();
    setPromptPopup(true);
    setPromptStatus(null);
  }, [fetchPrompts]);

  const updatePromptField = useCallback((path: string[], value: string) => {
    setPromptConfig(prev => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as PromptConfig;
      let obj: any = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  }, []);

  // ── Re-assign webcam stream after React renders the video element
  useEffect(() => {
    if (videoRef.current && webcamStreamRef.current) {
      videoRef.current.srcObject = webcamStreamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [webcamReady]);

  // ─── Server start / stop ──────────────────────────────────────────────────

  const startServer = useCallback(async () => {
    setError(null);
    try {
      await fetch(`${getApiBaseUrl()}/v1/lexa/start`, {
        method: 'POST',
        headers: authHeaders(),
      });
      await fetchServerStatus();
    } catch (e: any) {
      setError(`Server start failed: ${e.message}`);
    }
  }, [fetchServerStatus]);

  const stopServer = useCallback(async () => {
    setError(null);
    try {
      await fetch(`${getApiBaseUrl()}/v1/lexa/stop`, {
        method: 'POST',
        headers: authHeaders(),
      });
      await fetchServerStatus();
      setSessionState('idle');
    } catch (e: any) {
      setError(`Server stop failed: ${e.message}`);
    }
  }, [fetchServerStatus]);

  // ─── WebSocket connect/disconnect ─────────────────────────────────────────

  const connectWS = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    const wsUrl = `${getApiBaseUrl().replace(/^http/, 'ws')}/ws/lexa`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    wsReadyRef.current = false;

    ws.onopen = () => { wsReadyRef.current = true; };

    ws.onmessage = (evt) => {
      const msg = JSON.parse(evt.data);
      if (msg.type === 'session.created' || msg.type === 'session.init_ok') {
        setSessionState('ready');
      } else if (msg.type === 'session.reset') {
        setSessionState('idle');
      } else if (msg.type === 'response.created') {
        setSessionState('generating');
        // Only add placeholder if one isn't already present (client may have added it pre-send)
        setMessages((prev) => prev.some(m => m.isStreaming) ? prev : [...prev, { role: 'assistant', content: '', isStreaming: true }]);
      } else if (msg.type === 'response.text.delta') {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.isStreaming) next[next.length - 1] = { ...last, content: last.content + msg.delta };
          return next;
        });
      } else if (msg.type === 'response.done') {
        setSessionState('ready');
        webcamFrameInFlightRef.current = false;
        // Use round_idx from backend if provided, otherwise fallback to local counter
        const currentRound = typeof msg.round_idx === 'number' ? msg.round_idx : roundRef.current;
        setMessages((prev) => prev.map((m) => m.isStreaming ? { ...m, isStreaming: false } : m));
        setRoundIdx((r) => r + 1);
        // Fetch TTS audio if enabled and backend didn't flag no_tts
        if (params.use_tts && !msg.no_tts && !msg.skipped) {
          fetch(`${getApiBaseUrl()}/v1/lexa/tts_audio/${currentRound}`, { headers: authHeaders() })
            .then(r => r.ok ? r.blob() : null)
            .then(blob => {
              if (!blob) return;
              const audioUrl = URL.createObjectURL(blob);
              setMessages(prev => {
                const updated = [...prev];
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].role === 'assistant') {
                    updated[i] = { ...updated[i], audioUrl };
                    break;
                  }
                }
                return updated;
              });
            })
            .catch(() => {});
        }
      } else if (msg.type === 'is_listen') {
        setSessionState('listening');
      } else if (msg.type === 'error') {
        webcamFrameInFlightRef.current = false;
        setError(msg.message);
        setSessionState('ready');
      }
    };

    ws.onerror = () => { wsReadyRef.current = false; };
    ws.onclose = () => { wsReadyRef.current = false; };
  }, []);

  const disconnectWS = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    wsReadyRef.current = false;
  }, []);

  // ─── WebRTC full-duplex ───────────────────────────────────────────────────

  const startRTC = useCallback(async () => {
    if (rtcRef.current) return;
    setRtcStatus('connecting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      rtcMicStreamRef.current = stream;

      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      rtcRef.current = pc;

      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      // Receive AI audio
      pc.ontrack = (evt) => {
        const audio = rtcAudioRef.current ?? new Audio();
        rtcAudioRef.current = audio;
        audio.srcObject = evt.streams[0];
        audio.play().catch(() => {});
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
          setRtcStatus('error');
          setRtcActive(false);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') { resolve(); return; }
        pc.onicegatheringstatechange = () => { if (pc.iceGatheringState === 'complete') resolve(); };
        setTimeout(resolve, 3000);
      });

      const apiBase = window.location.protocol + '//' + window.location.hostname + ':8888';
      const resp = await fetch(`${apiBase}/v1/lexa/rtc/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': 't1ms4n8phIwu8IdaCn7M1A' },
        body: JSON.stringify({ sdp: pc.localDescription!.sdp, type: pc.localDescription!.type }),
      });
      if (!resp.ok) throw new Error(`RTC offer failed: ${resp.status}`);
      const answer = await resp.json();
      await pc.setRemoteDescription(new RTCSessionDescription(answer));

      setRtcActive(true);
      setRtcStatus('live');
    } catch (err) {
      setRtcStatus('error');
      setError(`WebRTC error: ${(err as Error).message}`);
      rtcRef.current?.close();
      rtcRef.current = null;
      rtcMicStreamRef.current?.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const stopRTC = useCallback(() => {
    rtcRef.current?.close();
    rtcRef.current = null;
    rtcMicStreamRef.current?.getTracks().forEach((t) => t.stop());
    rtcMicStreamRef.current = null;
    if (rtcAudioRef.current) { rtcAudioRef.current.srcObject = null; }
    setRtcActive(false);
    setRtcStatus('idle');
  }, []);

  // ─── Session init ─────────────────────────────────────────────────────────

  const initSession = useCallback(async () => {
    setError(null);
    // Always open WS first
    connectWS();
    await new Promise((r) => setTimeout(r, 400)); // brief wait for ws.onopen
    try {
      const body: Record<string, unknown> = {
        media_type: params.media_type,
        use_tts: params.use_tts,
        duplex_mode: params.duplex_mode,
        n_predict: params.n_predict,
        tts_gpu_layers: params.tts_gpu_layers,
        token2wav_device: params.token2wav_device,
        output_dir: params.output_dir,
      };
      if (params.voice_audio) body.voice_audio = params.voice_audio;

      const res = await fetch(`${getApiBaseUrl()}/v1/lexa/omni_init`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error ?? 'omni_init failed');
      setSessionState('ready');
      setRoundIdx(0);
    } catch (e: any) {
      setError(`Init failed: ${e.message}`);
    }
  }, [params]);

  // ─── Reset session ────────────────────────────────────────────────────────

  const resetSession = useCallback(async () => {
    setError(null);
    abortControllerRef.current?.abort();
    try {
      await fetch(`${getApiBaseUrl()}/v1/lexa/reset`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch { /* ignore */ }
    setSessionState('idle');
    setRoundIdx(0);
    setMessages([]);
    setPendingAttachments([]);
    stopWebcam();
  }, []);

  // ─── Apply config update ─────────────────────────────────────────────────

  const applySessionConfig = useCallback(async () => {
    setError(null);
    try {
      const body: Record<string, unknown> = {
        media_type: params.media_type,
        duplex_mode: params.duplex_mode,
        highImage: params.highImage,
        sliding_window_mode: params.sliding_window_mode !== 'none' ? params.sliding_window_mode : undefined,
        sliding_window_high_water: params.sliding_window_high_water,
        sliding_window_low_water: params.sliding_window_low_water,
      };
      if (params.voice_audio) body.voice_audio = params.voice_audio;
      await fetch(`${getApiBaseUrl()}/v1/lexa/update_session_config`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (e: any) {
      setError(`Config update failed: ${e.message}`);
    }
  }, [params]);

  // ─── Break generation ─────────────────────────────────────────────────────

  const breakGeneration = useCallback(async () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'break' }));
    } else {
      try {
        await fetch(`${getApiBaseUrl()}/v1/lexa/break`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'user_stop' }),
        });
      } catch { /* ignore */ }
    }
    setSessionState('ready');
    setMessages((prev) =>
      prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m))
    );
  }, []);

  // ─── Audio recording ──────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType });
        const url = URL.createObjectURL(blob);
        setPendingAttachments((prev) => [
          ...prev.filter((a) => a.type !== 'audio'),
          { type: 'audio', url, name: `recording-${Date.now()}.webm`, blob },
        ]);
        // Auto-send in Audio/Omni mode after recording stops
        if (autoSendAfterRecordRef.current) {
          autoSendAfterRecordRef.current = false;
          // Small delay to let setPendingAttachments propagate
          setTimeout(() => handleSendRef.current(), 100);
        }
      };
      mr.start(200);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch (e: any) {
      setError(`Microphone error: ${e.message}`);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      // In Audio/Omni mode, auto-send after recording stops
      if (params.mode === 'audio' || params.mode === 'omni') {
        autoSendAfterRecordRef.current = true;
      }
      stopRecording();
    }
    else startRecording();
  }, [isRecording, startRecording, stopRecording, params.mode]);

  // ─── Webcam ───────────────────────────────────────────────────────────────

  const startWebcam = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 448 }, height: { ideal: 448 }, frameRate: { max: 2 } }
      });
      webcamStreamRef.current = stream;
      setShowWebcam(true);      // render video element first
      setWebcamReady(true);     // then trigger the useEffect to assign stream
    } catch (e: any) {
      setError(`Webcam error: ${e.message}`);
    }
  }, []);

  const stopWebcam = useCallback(() => {
    // Stop auto-frame interval
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
    webcamFrameInFlightRef.current = false;
    webcamStreamRef.current?.getTracks().forEach((t) => t.stop());
    webcamStreamRef.current = null;
    setShowWebcam(false);
    setWebcamReady(false);
  }, []);

  // ── Auto-stream 1 FPS via WS when webcam active + WS open ─────────────────
  useEffect(() => {
    if (!webcamReady) return;
    // Start interval: 1 frame/sec = 1000ms
    frameIntervalRef.current = setInterval(() => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ws = wsRef.current;
      if (!video || !canvas || !ws || ws.readyState !== WebSocket.OPEN) return;
      if (webcamFrameInFlightRef.current) return;
      if (video.videoWidth === 0) return;
      // Resize to max 448x448 keeping aspect ratio
      const maxDim = 448;
      let w = video.videoWidth;
      let h = video.videoHeight;
      if (w > maxDim || h > maxDim) {
        const ratio = Math.min(maxDim / w, maxDim / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(video, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
          const reader = new FileReader();
          reader.onload = () => {
            const b64 = (reader.result as string).split(',')[1];
            webcamFrameInFlightRef.current = true;
            wsRef.current?.send(JSON.stringify({ type: 'video_frame', image: b64 }));
          };
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.7   // lighter quality for streaming
      );
    }, 1000); // 1 FPS

    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
    };
  }, [webcamReady]);

  const captureFrame = useCallback(() => {
    // Manual single-shot capture → add as attachment (for HTTP/text mode)
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !webcamReady) return;
    const maxDim = 448;
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w > maxDim || h > maxDim) {
      const ratio = Math.min(maxDim / w, maxDim / h);
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);
    }
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setPendingAttachments((prev) => [
          ...prev.filter((a) => a.type !== 'image'),
          { type: 'image', url, name: `frame-${Date.now()}.jpg`, blob },
        ]);
      },
      'image/jpeg',
      0.85
    );
  }, [webcamReady]);

  // ─── Image file picker ────────────────────────────────────────────────────

  const handleImageFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type.startsWith('video/')) {
      // ── Full video processing: extract frames at ~0.5 FPS, queue via WS ──
      const videoEl = document.createElement('video');
      videoEl.preload = 'auto';
      videoEl.src = URL.createObjectURL(file);
      videoEl.muted = true;

      videoEl.onloadedmetadata = async () => {
        const duration = videoEl.duration;
        const fps = 0.5; // 1 frame every 2 seconds
        const maxFrames = 45; // safety cap
        const interval = 1 / fps;
        const totalFrames = Math.min(Math.ceil(duration * fps), maxFrames);

        // Add user message indicating video upload
        const durationStr = duration >= 60
          ? `${Math.floor(duration / 60)}m ${Math.round(duration % 60)}s`
          : `${Math.round(duration)}s`;
        setMessages(prev => [...prev,
          { role: 'user', content: `📹 Video: ${file.name} (${durationStr}, ${totalFrames} frames)` }
        ]);

        setVideoProcessing({ active: true, current: 0, total: totalFrames });
        videoProcessingAbortRef.current = false;

        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          setError('WebSocket not connected — init session first');
          setVideoProcessing({ active: false, current: 0, total: 0 });
          return;
        }

        const canvas = document.createElement('canvas');
        const maxDim = 448;

        for (let i = 0; i < totalFrames; i++) {
          if (videoProcessingAbortRef.current) break;

          const seekTime = Math.min(i * interval, duration - 0.1);
          videoEl.currentTime = seekTime;

          // Wait for seek to complete
          await new Promise<void>((resolve) => {
            videoEl.onseeked = () => resolve();
          });

          // Capture frame
          let w = videoEl.videoWidth;
          let h = videoEl.videoHeight;
          if (w > maxDim || h > maxDim) {
            const ratio = Math.min(maxDim / w, maxDim / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d')!.drawImage(videoEl, 0, 0, w, h);

          const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.7)
          );

          if (!blob || videoProcessingAbortRef.current) break;

          // Convert to base64 and send via WS as video_frame
          const b64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.readAsDataURL(blob);
          });

          // Wait for WS to be ready (previous frame done)
          while (webcamFrameInFlightRef.current && !videoProcessingAbortRef.current) {
            await new Promise(r => setTimeout(r, 200));
          }
          if (videoProcessingAbortRef.current) break;

          webcamFrameInFlightRef.current = true;
          ws.send(JSON.stringify({ type: 'video_frame', image: b64 }));

          setVideoProcessing(prev => ({ ...prev, current: i + 1 }));

          // Wait for response.done before next frame
          await new Promise<void>((resolve) => {
            const check = setInterval(() => {
              if (!webcamFrameInFlightRef.current || videoProcessingAbortRef.current) {
                clearInterval(check);
                resolve();
              }
            }, 200);
          });
        }

        URL.revokeObjectURL(videoEl.src);
        setVideoProcessing({ active: false, current: 0, total: 0 });
      };
    } else {
      const url = URL.createObjectURL(file);
      setPendingAttachments((prev) => [
        ...prev.filter((a) => a.type !== 'image'),
        { type: 'image', url, name: file.name, blob: file },
      ]);
    }
    e.target.value = '';
  }, []);

  const removeAttachment = useCallback((type: 'audio' | 'image') => {
    setPendingAttachments((prev) => {
      const removed = prev.find((a) => a.type === type);
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((a) => a.type !== type);
    });
  }, []);

  // ─── Text-mode send (OpenAI /v1/chat/completions) ─────────────────────────

  const sendTextMode = useCallback(
    async (userMessage: Message) => {
      const history = [...messages, userMessage];
      const assistantId = uid();
      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: 'assistant', content: '', isStreaming: true },
      ]);

      const ctrl = new AbortController();
      abortControllerRef.current = ctrl;
      setSessionState('generating');

      try {
        const chatMessages = [
          { role: 'system', content: params.systemPrompt },
          ...history.map((m) => ({ role: m.role, content: m.content })),
        ];

        const res = await fetch(`${getApiBaseUrl()}/v1/lexa/chat`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: params.model,
            messages: chatMessages,
            temperature: params.temperature,
            top_p: params.top_p,
            max_tokens: params.max_tokens,
            stream: params.stream,
          }),
          signal: ctrl.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        if (params.stream) {
          const reader = res.body!.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          let full = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() ?? '';
            for (const line of lines) {
              // Handle both "data: " (OpenAI) and "data:" (OllamaCloud/g4f.space) formats
              if (!line.startsWith('data:')) continue;
              const payload = line.replace(/^data:\s*/, '').trim();
              if (payload === '[DONE]') break;
              try {
                const parsed = JSON.parse(payload);
                const delta = parsed.choices?.[0]?.delta;
                // Extract both content and reasoning_content (OllamaCloud models)
                const content = delta?.content ?? '';
                const reasoning = delta?.reasoning_content ?? '';
                const text = reasoning + content;
                if (text) {
                  full += text;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId ? { ...m, content: full } : m
                    )
                  );
                }
              } catch { /* skip malformed */ }
            }
          }
        } else {
          const data = await res.json();
          const content = data.choices?.[0]?.message?.content ?? '';
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content } : m
            )
          );
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          setError(`Generation failed: ${e.message}`);
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
        }
      } finally {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
        );
        setSessionState('ready');
        abortControllerRef.current = null;
      }
    },
    [messages, params]
  );

  // ─── Omni/Audio mode send ─────────────────────────────────────────────────

  const sendOmniMode = useCallback(
    async (userMessage: Message, attachments: Attachment[]) => {
      const ws = wsRef.current;

      // ── WebSocket path (preferred) ──────────────────────────────────────
      if (ws && ws.readyState === WebSocket.OPEN) {
        setSessionState('prefilling');
        const audioAtt = attachments.find((a) => a.type === 'audio');
        const imageAtt = attachments.find((a) => a.type === 'image');

        const toB64 = (blob: Blob): Promise<string> =>
          new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload = () => res((reader.result as string).split(',')[1]);
            reader.onerror = rej;
            reader.readAsDataURL(blob);
          });

        if (audioAtt) {
          const b64 = await toB64(audioAtt.blob);
          ws.send(JSON.stringify({ type: 'audio_chunk', audio: b64 }));
        } else if (imageAtt && params.mode === 'omni') {
          const b64 = await toB64(imageAtt.blob);
          webcamFrameInFlightRef.current = true;
          ws.send(JSON.stringify({ type: 'video_frame', image: b64 }));
        } else {
          // text fallback via WS — include system_prompt, history, and params
          const history = messages
            .filter(m => !m.isStreaming)
            .slice(-10)
            .map(m => ({ role: m.role, content: m.content }));
          ws.send(JSON.stringify({
            type: 'text',
            content: userMessage.content,
            system_prompt: params.systemPrompt,
            history,
            temperature: params.temperature,
            top_p: params.top_p,
            max_tokens: params.max_tokens,
          }));
        }
        // Add assistant placeholder now so user sees streaming immediately
        setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);
        setSessionState('generating');
        // response.created / delta / done handled in ws.onmessage
        return;
      }

      // ── HTTP SSE fallback ───────────────────────────────────────────────
      const currentRound = roundIdx;
      const assistantId = uid();

      setSessionState('prefilling');
      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: 'assistant', content: '', isStreaming: true, roundIdx: currentRound },
      ]);

      try {
        const form = new FormData();
        form.append('cnt', '1');
        form.append('max_slice_nums', String(params.max_slice_nums));

        const audioAtt2 = attachments.find((a) => a.type === 'audio');
        const imageAtt2 = attachments.find((a) => a.type === 'image');
        if (audioAtt2) form.append('audio', blobToFile(audioAtt2.blob, audioAtt2.name));
        if (imageAtt2 && params.mode === 'omni') form.append('image', blobToFile(imageAtt2.blob, imageAtt2.name));

        const prefillRes = await fetch(`${getApiBaseUrl()}/v1/lexa/prefill`, {
          method: 'POST', headers: authHeaders(), body: form,
        });
        const prefillData = await prefillRes.json();
        if (!prefillData.success) throw new Error(prefillData.error ?? 'prefill failed');

        setSessionState('generating');
        const ctrl = new AbortController();
        abortControllerRef.current = ctrl;

        const decodeRes = await fetch(`${getApiBaseUrl()}/v1/lexa/decode`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ debug_dir: params.output_dir, round_idx: currentRound }),
          signal: ctrl.signal,
        });
        if (!decodeRes.ok) throw new Error(`Decode HTTP ${decodeRes.status}`);

        const reader = decodeRes.body!.getReader();
        const decoder = new TextDecoder();
        let buf = ''; let full = ''; let isListening = false; let ended = false;

        while (!ended) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n'); buf = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const payload = line.slice(6).trim();
            if (payload === '[DONE]') { ended = true; break; }
            try {
              const evt = JSON.parse(payload) as { content: string; stop: boolean; is_listen: boolean; end_of_turn: boolean; };
              if (evt.content) { full += evt.content; setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: full } : m)); }
              if (evt.is_listen && params.duplex_mode) { isListening = true; setSessionState('listening'); }
              if (evt.stop || evt.end_of_turn) { ended = true; break; }
            } catch { /* skip */ }
          }
        }

        if (params.use_tts) {
          try {
            const ttsRes = await fetch(`${getApiBaseUrl()}/v1/lexa/tts_audio/${currentRound}`, { headers: authHeaders() });
            if (ttsRes.ok) {
              const audioUrl = URL.createObjectURL(await ttsRes.blob());
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, audioUrl, isStreaming: false } : m));
            }
          } catch { /* TTS optional */ }
        }

        setRoundIdx((r) => r + 1);
        setSessionState(isListening ? 'listening' : 'ready');
      } catch (e: any) {
        if (e.name !== 'AbortError') { setError(`Omni send failed: ${e.message}`); setMessages((prev) => prev.filter((m) => m.id !== assistantId)); }
        setSessionState('ready');
      } finally {
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)));
        abortControllerRef.current = null;
      }
    },
    [roundIdx, params]
  );

  // ─── Main send handler ────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    const text = input.trim();
    const attachments = [...pendingAttachments];

    if (!text && attachments.length === 0) return;
    if (sessionState === 'generating' || sessionState === 'prefilling') return;

    setError(null);

    const userMsg: Message = {
      id: uid(),
      role: 'user',
      content: text,
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setPendingAttachments([]);

    if (params.mode === 'text') {
      await sendTextMode(userMsg);
    } else {
      // Auto-init session if not ready
      if (sessionState === 'idle') {
        await initSession();
        // initSession sets sessionState to 'ready' or throws — check after
      }
      await sendOmniMode(userMsg, attachments);
    }
  }, [input, pendingAttachments, sessionState, params.mode, sendTextMode, sendOmniMode, initSession]);

  // Keep handleSendRef in sync so onstop auto-send works
  useEffect(() => { handleSendRef.current = handleSend; }, [handleSend]);

  // ─── Keyboard shortcut ────────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // ─── Cleanup on unmount ───────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      webcamStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // ─── Derived UI flags ─────────────────────────────────────────────────────

  const isOmniMode = params.mode !== 'text';
  const isGenerating = sessionState === 'generating' || sessionState === 'prefilling';
  const needsSession = isOmniMode && sessionState === 'idle';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-200 text-sm select-none">
      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <header className="flex items-center gap-2 px-3 py-2 bg-slate-900 border-b border-slate-800 flex-shrink-0 flex-wrap">
        <span className="font-bold text-purple-400 tracking-tight flex items-center gap-1">
          🤖 <span>Lexa Omni</span>
          <span className="text-slate-500 text-xs font-normal">v2</span>
        </span>

        {/* Mode selector */}
        <div className="flex rounded overflow-hidden border border-slate-700 text-xs">
          {(['text', 'audio', 'omni'] as ApiMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setParam('mode', m)}
              className={`px-2 py-0.5 capitalize transition-colors ${
                params.mode === m
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        {/* Session badge */}
        <SessionBadge state={sessionState} />

        {/* Round counter */}
        {isOmniMode && sessionState !== 'idle' && (
          <span className="text-xs text-slate-500">r#{roundIdx}</span>
        )}

        {/* Server control */}
        <div className="flex items-center gap-1 ml-1">
          <span
            className={`w-2 h-2 rounded-full ${
              serverRunning === null
                ? 'bg-slate-500'
                : serverRunning
                ? 'bg-green-500'
                : 'bg-red-500'
            }`}
          />
          <button
            onClick={startServer}
            disabled={serverRunning === true}
            className="px-2 py-0.5 text-xs bg-green-800 hover:bg-green-700 disabled:opacity-40 rounded"
          >
            ▶ Start
          </button>
          <button
            onClick={stopServer}
            disabled={serverRunning === false}
            className="px-2 py-0.5 text-xs bg-red-800 hover:bg-red-700 disabled:opacity-40 rounded"
          >
            ■ Stop
          </button>
        </div>

        {/* WebRTC full-duplex button */}
        <div className="flex items-center gap-1 ml-1">
          <span
            className={`w-2 h-2 rounded-full ${
              rtcStatus === 'idle' ? 'bg-slate-500'
              : rtcStatus === 'connecting' ? 'bg-yellow-400 animate-pulse'
              : rtcStatus === 'live' ? 'bg-cyan-400'
              : 'bg-red-500'
            }`}
          />
          {rtcActive ? (
            <button
              onClick={stopRTC}
              className="px-2 py-0.5 text-xs bg-cyan-800 hover:bg-cyan-700 rounded font-bold"
              title="Wyłącz WebRTC (mikrofon na żywo)"
            >
              🔴 RTC
            </button>
          ) : (
            <button
              onClick={startRTC}
              disabled={rtcStatus === 'connecting'}
              className="px-2 py-0.5 text-xs bg-slate-700 hover:bg-cyan-800 disabled:opacity-40 rounded"
              title="WebRTC full-duplex — mikrofon live do Lexa"
            >
              🎙 RTC
            </button>
          )}
        </div>

        <div className="flex-1" />

        {/* System Prompt Manager */}
        <button
          onClick={openPromptPopup}
          title="System Prompt Manager — edytuj prompty C++/Chat/Omni"
          className="px-2 py-0.5 text-xs rounded border border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all font-bold"
        >
          🧠 Prompts
        </button>

        {/* Params toggle */}
        <button
          onClick={() => setShowParams((v) => !v)}
          className={`px-2 py-0.5 text-xs rounded border ${
            showParams
              ? 'bg-purple-600/30 border-purple-500 text-purple-300'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'
          }`}
        >
          ⚙ Params
        </button>

        {/* Reset */}
        <button
          onClick={resetSession}
          className="px-2 py-0.5 text-xs bg-slate-800 border border-slate-700 hover:bg-red-900 hover:border-red-700 text-slate-400 hover:text-red-300 rounded"
          title="Reset session"
        >
          🗑
        </button>
      </header>

      {/* ════════════ SYSTEM PROMPT MANAGER POPUP ════════════════════════════ */}
      {promptPopup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setPromptPopup(false)}>
          <div
            className="bg-[#131722] border border-[#2a2e39] rounded-2xl shadow-2xl shadow-purple-900/30 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Popup header */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#2a2e39] bg-gradient-to-r from-[#1a1f2e] to-[#131722]">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <span className="text-sm">🧠</span>
              </div>
              <div className="flex-1">
                <h2 className="text-sm font-black text-white uppercase tracking-wider">System Prompt Manager</h2>
                <p className="text-[9px] text-slate-500">Edytuj prompty Lexa — Chat, Omni Duplex, Omni Simplex (EN/ZH)</p>
              </div>
              {promptConfig && (
                <span className="text-[8px] text-slate-600 font-mono">v{promptConfig.version}</span>
              )}
              <button onClick={() => setPromptPopup(false)} className="w-7 h-7 rounded-lg bg-[#1e222d] text-slate-500 hover:text-white hover:bg-red-500/30 flex items-center justify-center transition-all">
                ✕
              </button>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 px-5 pt-3 pb-2 border-b border-[#1e222d]">
              {([['chat','Chat Prompt','purple'],['duplex','Omni Duplex','blue'],['simplex_en','Omni EN','emerald'],['simplex_zh','Omni ZH','amber']] as const).map(([id, label, color]) => (
                <button
                  key={id}
                  onClick={() => setPromptTab(id as PromptTab)}
                  className={`text-[9px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-wider transition-all ${
                    promptTab === id
                      ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/40`
                      : 'text-slate-500 hover:text-slate-300 border border-transparent hover:border-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
              {!promptConfig ? (
                <div className="flex items-center justify-center h-32 text-slate-500 text-xs">
                  ⏳ Ładuję konfigurację...
                </div>
              ) : promptTab === 'chat' ? (
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-purple-400 uppercase tracking-wider block">Chat System Prompt</label>
                  <textarea
                    value={promptConfig.chat_system_prompt}
                    onChange={e => updatePromptField(['chat_system_prompt'], e.target.value)}
                    className="w-full bg-[#0d1117] text-slate-300 text-[10px] border border-[#2a2e39] rounded-xl px-4 py-3 resize-none font-mono leading-relaxed focus:outline-none focus:border-purple-500/60"
                    rows={6}
                    placeholder="System prompt dla trybu czatu..."
                  />
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mt-3">Server System Prompt <span className="text-[8px] font-normal text-slate-600">(opcjonalny — --system-prompt)</span></label>
                  <textarea
                    value={promptConfig.server_system_prompt}
                    onChange={e => updatePromptField(['server_system_prompt'], e.target.value)}
                    className="w-full bg-[#0d1117] text-slate-300 text-[10px] border border-[#2a2e39] rounded-xl px-4 py-3 resize-none font-mono leading-relaxed focus:outline-none focus:border-slate-500/60"
                    rows={3}
                    placeholder="Opcjonalny server system prompt..."
                  />
                </div>
              ) : (
                (() => {
                  const key = promptTab as 'duplex' | 'simplex_en' | 'simplex_zh';
                  const pair = promptConfig.omni_prompts[key];
                  const titles: Record<string, string> = {
                    duplex: 'Duplex (Real-time streaming)',
                    simplex_en: 'Simplex English (voice cloning)',
                    simplex_zh: 'Simplex Chinese (voice cloning)',
                  };
                  return (
                    <div className="space-y-4">
                      <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">{titles[key]}</p>
                      <div className="space-y-1">
                        <label className="text-[9px] text-slate-500 font-bold uppercase tracking-wider block">🎙️ Voice Clone Prompt <span className="text-[8px] font-normal text-slate-600">(prefix)</span></label>
                        <textarea
                          value={pair.voice_clone_prompt}
                          onChange={e => updatePromptField(['omni_prompts', key, 'voice_clone_prompt'], e.target.value)}
                          className="w-full bg-[#0d1117] text-emerald-300 text-[10px] border border-[#2a2e39] rounded-xl px-4 py-3 resize-none font-mono leading-relaxed focus:outline-none focus:border-emerald-500/60"
                          rows={4}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] text-slate-500 font-bold uppercase tracking-wider block">🤖 Assistant Prompt <span className="text-[8px] font-normal text-slate-600">(suffix)</span></label>
                        <textarea
                          value={pair.assistant_prompt}
                          onChange={e => updatePromptField(['omni_prompts', key, 'assistant_prompt'], e.target.value)}
                          className="w-full bg-[#0d1117] text-amber-300 text-[10px] border border-[#2a2e39] rounded-xl px-4 py-3 resize-none font-mono leading-relaxed focus:outline-none focus:border-amber-500/60"
                          rows={4}
                        />
                      </div>
                      <div className="bg-[#0d1117] border border-[#1e222d] rounded-lg px-3 py-2">
                        <p className="text-[8px] text-slate-600 font-mono">
                          <span className="text-blue-400/60">Format C++:</span>{' '}
                          <span className="text-emerald-400/50">[voice_clone_prompt]</span>
                          <span className="text-slate-600"> → </span>
                          <span className="text-purple-400/50">[ref_audio_emb]</span>
                          <span className="text-slate-600"> → </span>
                          <span className="text-amber-400/50">[assistant_prompt]</span>
                          <span className="text-slate-600"> → </span>
                          <span className="text-pink-400/50">[user_input]</span>
                        </p>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-[#2a2e39] bg-[#0d1117] flex items-center gap-3">
              {promptStatus && <span className="text-[9px] text-slate-400 flex-1 truncate">{promptStatus}</span>}
              {!promptStatus && <div className="flex-1" />}
              <button
                onClick={() => promptConfig && savePrompts(promptConfig)}
                disabled={promptSaving || promptApplying || !promptConfig}
                className="text-[9px] px-4 py-2 rounded-lg border border-purple-500/40 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 font-bold uppercase tracking-wider transition-all disabled:opacity-50"
              >
                {promptSaving ? '⏳ Zapisuję...' : '💾 Zapisz'}
              </button>
              <button
                onClick={() => promptConfig && applyPromptsAndRestart(promptConfig)}
                disabled={promptSaving || promptApplying || !promptConfig}
                className="text-[9px] px-4 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 font-bold uppercase tracking-wider transition-all disabled:opacity-50"
              >
                {promptApplying ? '⏳ Restartuję...' : '🔄 Zapisz + Restart'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ERROR BANNER ───────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-950 border-b border-red-800 text-red-300 text-xs">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-300">
            ✕
          </button>
        </div>
      )}

      {/* ── BODY ───────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── CHAT AREA ─────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
          >
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-slate-600 text-xs gap-2 pt-8">
                <span className="text-3xl">🤖</span>
                <span>Lexa Omni MiniCPM-o 4.5</span>
                {needsSession && (
                  <button
                    onClick={initSession}
                    className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg shadow"
                  >
                    Init Session →
                  </button>
                )}
              </div>
            )}

            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                base={getApiBaseUrl()}
                authH={authHeaders()}
              />
            ))}

            {/* Duplex listening indicator */}
            {sessionState === 'listening' && (
              <div className="flex items-center gap-2 text-yellow-400 text-xs animate-pulse px-2">
                <span className="w-2 h-2 rounded-full bg-yellow-400" />
                🎤 Model is listening…
              </div>
            )}
          </div>

          {/* Webcam preview */}
          {showWebcam && (
            <div className="relative mx-3 mb-2 rounded-lg overflow-hidden border border-slate-700 bg-black max-h-40">
              <video ref={videoRef} className="w-full h-40 object-cover" muted playsInline />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                <button
                  onClick={captureFrame}
                  disabled={!webcamReady}
                  className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-40 rounded shadow"
                >
                  📸 Capture
                </button>
                <button
                  onClick={stopWebcam}
                  className="px-3 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded shadow"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          {/* Hidden canvas for non-webcam capture */}
          {!showWebcam && <canvas ref={canvasRef} className="hidden" />}

          {/* Pending attachments */}
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 px-3 pb-1">
              {pendingAttachments.map((att) => (
                <div
                  key={att.type}
                  className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded px-2 py-0.5 text-xs"
                >
                  {att.type === 'image' ? (
                    <img src={att.url} alt={att.name} className="w-8 h-8 object-cover rounded" />
                  ) : (
                    <span>🎵 {att.name}</span>
                  )}
                  <button
                    onClick={() => removeAttachment(att.type)}
                    className="text-slate-500 hover:text-red-400 ml-1"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* VIDEO PROCESSING PROGRESS */}
          {videoProcessing.active && (
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-950/60 border-t border-indigo-800">
              <span className="text-xs text-indigo-300">
                📹 Processing frame {videoProcessing.current}/{videoProcessing.total}
              </span>
              <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 transition-all duration-300"
                  style={{ width: `${(videoProcessing.current / videoProcessing.total) * 100}%` }}
                />
              </div>
              <button
                onClick={() => { videoProcessingAbortRef.current = true; }}
                className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded font-bold"
              >
                ✕ Cancel
              </button>
            </div>
          )}

          {/* INPUT ROW */}
          <div className="flex items-end gap-1.5 px-3 py-2 border-t border-slate-800 bg-slate-900 flex-shrink-0">
            {/* Image picker */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200 flex-shrink-0"
              title="Attach image"
            >
              📎
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleImageFile}
            />

            {/* Mic button */}
            <button
              onClick={toggleRecording}
              className={`p-1.5 rounded flex-shrink-0 ${
                isRecording
                  ? 'bg-red-600 text-white animate-pulse'
                  : 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
              title={isRecording ? 'Stop recording' : 'Record audio'}
            >
              🎤
            </button>

            {/* Webcam button */}
            <button
              onClick={showWebcam ? stopWebcam : startWebcam}
              className={`p-1.5 rounded flex-shrink-0 ${
                showWebcam
                  ? 'bg-blue-600 text-white'
                  : 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
              title="Webcam capture"
            >
              📹
            </button>

            {/* Text input */}
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isGenerating
                  ? 'Generating…'
                  : needsSession
                  ? 'Init session first…'
                  : 'Message Lexa… (Shift+Enter for newline)'
              }
              disabled={isGenerating || (isOmniMode && sessionState === 'idle')}
              rows={1}
              className="flex-1 resize-none bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded px-3 py-1.5 focus:outline-none focus:border-purple-500 placeholder-slate-600 disabled:opacity-50 min-h-[36px] max-h-32 overflow-y-auto"
              style={{ height: 'auto' }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
              }}
            />

            {/* STOP or SEND */}
            {isGenerating ? (
              <button
                onClick={breakGeneration}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded flex-shrink-0 animate-pulse font-bold shadow"
              >
                ■ STOP
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim() && pendingAttachments.length === 0}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white text-xs rounded flex-shrink-0 font-bold shadow"
              >
                SEND
              </button>
            )}
          </div>

          {/* Init session prompt bar */}
          {needsSession && (
            <div className="px-3 py-2 bg-slate-900/80 border-t border-slate-800 flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-slate-400">
                Omni/Audio mode requires an active session.
              </span>
              <button
                onClick={initSession}
                className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded font-bold"
              >
                Init Session
              </button>
            </div>
          )}
        </div>

        {/* ── PARAMS PANEL ──────────────────────────────────────── */}
        {showParams && (
          <ParamsPanel
            params={params}
            setParam={setParam}
            paramsTab={paramsTab}
            setParamsTab={setParamsTab}
            sessionState={sessionState}
            onInitSession={initSession}
            onApplyConfig={applySessionConfig}
            voiceFileRef={voiceFileRef}
          />
        )}
      </div>
    </div>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────

const MessageBubble: React.FC<{
  msg: Message;
  base: string;
  authH: Record<string, string>;
}> = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? 'bg-purple-600/30 border border-purple-500/30 text-slate-200'
            : 'bg-slate-800 border border-slate-700 text-slate-200'
        }`}
      >
        {/* Role label */}
        <div className={`text-xs font-semibold mb-1 ${isUser ? 'text-purple-300' : 'text-slate-400'}`}>
          {isUser ? 'You' : '🤖 Lexa'}
          {msg.roundIdx !== undefined && (
            <span className="ml-1 text-slate-600">r#{msg.roundIdx}</span>
          )}
        </div>

        {/* Image attachments */}
        {msg.attachments?.filter((a) => a.type === 'image').map((att) => (
          <img
            key={att.name}
            src={att.url}
            alt={att.name}
            className="max-w-xs rounded mb-2 border border-slate-600"
          />
        ))}

        {/* Audio attachments (user recorded) */}
        {msg.attachments?.filter((a) => a.type === 'audio').map((att) => (
          <audio key={att.name} src={att.url} controls className="mb-2 w-full max-w-xs" />
        ))}

        {/* Text content */}
        {msg.content ? (
          <span className="whitespace-pre-wrap break-words">{msg.content}</span>
        ) : msg.isStreaming ? (
          <span className="text-slate-500 animate-pulse">▌</span>
        ) : null}

        {/* Streaming indicator */}
        {msg.isStreaming && msg.content && (
          <span className="inline-block w-1.5 h-4 bg-purple-400 animate-pulse ml-0.5 align-text-bottom" />
        )}

        {/* TTS audio output */}
        {msg.audioUrl && (
          <div className="mt-2">
            <audio src={msg.audioUrl} controls autoPlay className="w-full h-8" style={{filter: 'invert(0.8)'}} />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Params Panel ─────────────────────────────────────────────────────────────

const TABS: { id: ParamsTab; label: string }[] = [
  { id: 'chat', label: 'Chat' },
  { id: 'omni', label: 'Omni' },
  { id: 'tts', label: 'TTS' },
  { id: 'vision', label: 'Vision' },
  { id: 'slidewin', label: 'SlideWin' },
  { id: 'dirs', label: 'Dirs' },
];

const ParamsPanel: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(key: K, value: OmniParams[K]) => void;
  paramsTab: ParamsTab;
  setParamsTab: (t: ParamsTab) => void;
  sessionState: SessionState;
  onInitSession: () => void;
  onApplyConfig: () => void;
  voiceFileRef: React.RefObject<HTMLInputElement>;
}> = ({
  params,
  setParam,
  paramsTab,
  setParamsTab,
  sessionState,
  onInitSession,
  onApplyConfig,
  voiceFileRef,
}) => {
  return (
    <aside className="w-64 flex-shrink-0 flex flex-col border-l border-slate-800 bg-slate-900 overflow-hidden">
      {/* Tab row */}
      <div className="flex border-b border-slate-800 flex-shrink-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setParamsTab(t.id)}
            className={`px-2 py-1.5 text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
              paramsTab === t.id
                ? 'bg-slate-800 text-purple-300 border-b-2 border-purple-500'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {paramsTab === 'chat' && (
          <ChatParamsTab params={params} setParam={setParam} />
        )}
        {paramsTab === 'omni' && (
          <OmniParamsTab params={params} setParam={setParam} />
        )}
        {paramsTab === 'tts' && (
          <TtsParamsTab params={params} setParam={setParam} voiceFileRef={voiceFileRef} />
        )}
        {paramsTab === 'vision' && (
          <VisionParamsTab params={params} setParam={setParam} />
        )}
        {paramsTab === 'slidewin' && (
          <SlideWinParamsTab params={params} setParam={setParam} />
        )}
        {paramsTab === 'dirs' && (
          <DirsParamsTab
            params={params}
            setParam={setParam}
            onApply={onApplyConfig}
          />
        )}
      </div>

      {/* Init Session button */}
      <div className="p-3 border-t border-slate-800 flex-shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Session:</span>
          <SessionBadge state={sessionState} />
        </div>
        <button
          onClick={onInitSession}
          disabled={sessionState !== 'idle'}
          className="w-full py-1.5 text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded font-bold"
        >
          Init Session
        </button>
      </div>
    </aside>
  );
};

// ─── Tab: Chat ────────────────────────────────────────────────────────────────

const ChatParamsTab: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(k: K, v: OmniParams[K]) => void;
}> = ({ params, setParam }) => (
  <>
    <LabeledInput label="Model" info="Nazwa modelu wysyłana do API. Dla llama-server nieistotna — używa załadowanego modelu.">
      <input
        type="text"
        value={params.model}
        onChange={(e) => setParam('model', e.target.value)}
        className={inputCls}
      />
    </LabeledInput>

    <SliderInput
      label="Temperature"
      info="Losowość odpowiedzi. Niska (0.1–0.5) = precyzyjnie, wysoka (0.8–2.0) = kreatywnie. Domyślnie 0.7."
      value={params.temperature}
      min={0}
      max={2}
      step={0.1}
      onChange={(v) => setParam('temperature', v)}
    />

    <SliderInput
      label="Top P"
      info="Nucleus sampling — uwzględnia tylko tokeny z łącznym prawdopodobieństwem P. 0.9 = dobry balans, 1.0 = wyłączone."
      value={params.top_p}
      min={0}
      max={1}
      step={0.05}
      onChange={(v) => setParam('top_p', v)}
    />

    <LabeledInput label="Max Tokens" info="Maksymalna długość odpowiedzi w tokenach. 1 token ≈ 0.75 słowa. Wyżej = dłuższe odpowiedzi, więcej VRAM.">
      <input
        type="number"
        value={params.max_tokens}
        onChange={(e) => setParam('max_tokens', Number(e.target.value))}
        min={64}
        max={32768}
        className={inputCls}
      />
    </LabeledInput>

    <LabeledInput label="Stream" info="Czy odpowiedź pojawia się token po tokenie (ON) czy cała naraz po zakończeniu (OFF). ON = lepszy UX.">
      <Toggle
        checked={params.stream}
        onChange={(v) => setParam('stream', v)}
        label={params.stream ? 'On' : 'Off'}
      />
    </LabeledInput>

    <LabeledInput label="System Prompt" info="Instrukcja systemowa definiująca zachowanie i osobowość modelu. Wysyłana jako pierwsza wiadomość przy każdej sesji.">
      <textarea
        value={params.systemPrompt}
        onChange={(e) => setParam('systemPrompt', e.target.value)}
        rows={4}
        className={`${inputCls} resize-none`}
      />
    </LabeledInput>
  </>
);

// ─── Tab: Omni ────────────────────────────────────────────────────────────────

const OmniParamsTab: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(k: K, v: OmniParams[K]) => void;
}> = ({ params, setParam }) => (
  <>
    <LabeledInput label="Media Type" info="Typ modalności sesji omni. 1=tylko audio (bez wizji), 2=audio+obraz/wideo. Musi pasować do trybu Audio/Omni.">
      <div className="flex gap-3">
        {[
          { val: 1, label: '1 — Audio only' },
          { val: 2, label: '2 — Audio+Vision' },
        ].map(({ val, label }) => (
          <label key={val} className="flex items-center gap-1 text-xs cursor-pointer">
            <input
              type="radio"
              checked={params.media_type === val}
              onChange={() => setParam('media_type', val)}
              className="accent-purple-500"
            />
            {label}
          </label>
        ))}
      </div>
    </LabeledInput>

    <LabeledInput label="TTS" info="Text-to-Speech — model generuje odpowiedź głosową (WAV). Wymaga modeli tts/ i token2wav-gguf/. Zużywa ~1 GB VRAM.">
      <Toggle
        checked={params.use_tts}
        onChange={(v) => setParam('use_tts', v)}
        label={params.use_tts ? 'Enabled' : 'Disabled'}
      />
    </LabeledInput>

    <LabeledInput label="Duplex Mode" info="Full-duplex: model może być przerywany w trakcie mówienia (jak prawdziwa rozmowa). Gdy model sygnalizuje IS_LISTEN, możesz mówić. Eksperymentalne.">
      <Toggle
        checked={params.duplex_mode}
        onChange={(v) => setParam('duplex_mode', v)}
        label={params.duplex_mode ? 'Full-duplex' : 'Turn-based'}
      />
    </LabeledInput>

    <LabeledInput label="N Predict" info="Maksymalna liczba tokenów do wygenerowania w jednej turze omni. Chroni przed niekończącą się halucynacją. Domyślnie 2048.">
      <input
        type="number"
        value={params.n_predict}
        onChange={(e) => setParam('n_predict', Number(e.target.value))}
        min={256}
        max={8192}
        step={256}
        className={inputCls}
      />
    </LabeledInput>
  </>
);

// ─── Tab: TTS ─────────────────────────────────────────────────────────────────

const TtsParamsTab: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(k: K, v: OmniParams[K]) => void;
  voiceFileRef: React.RefObject<HTMLInputElement>;
}> = ({ params, setParam, voiceFileRef }) => (
  <>
    <SliderInput
      label="TTS GPU Layers"
      info="Ile warstw modelu TTS załadować na GPU. 99=wszystko na GPU (szybko), 0=tylko CPU (wolno, mniej VRAM). Zmień tylko jeśli masz mało VRAM."
      value={params.tts_gpu_layers}
      min={0}
      max={99}
      step={1}
      onChange={(v) => setParam('tts_gpu_layers', v)}
    />

    <LabeledInput label="Token2Wav Device" info="Urządzenie dla modelu konwersji tokenów TTS na audio WAV. gpu:0=główna karta, gpu:1=druga karta, cpu=bez GPU.">
      <select
        value={params.token2wav_device}
        onChange={(e) => setParam('token2wav_device', e.target.value)}
        className={inputCls}
      >
        <option value="gpu:0">gpu:0</option>
        <option value="gpu:1">gpu:1</option>
        <option value="cpu">cpu</option>
      </select>
    </LabeledInput>

    <LabeledInput label="Voice Audio Path" info="Ścieżka do referencyjnego pliku WAV dla klonowania głosu. Model będzie naśladował ten głos podczas TTS. Plik musi być na serwerze.">
      <div className="flex gap-1">
        <input
          type="text"
          value={params.voice_audio}
          onChange={(e) => setParam('voice_audio', e.target.value)}
          placeholder="/path/to/voice.wav"
          className={`${inputCls} flex-1`}
        />
        <button
          onClick={() => voiceFileRef.current?.click()}
          className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded border border-slate-600"
          title="Pick file"
        >
          📂
        </button>
      </div>
      <input
        ref={voiceFileRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setParam('voice_audio', f.name);
          e.target.value = '';
        }}
      />
    </LabeledInput>
  </>
);

// ─── Tab: Vision ──────────────────────────────────────────────────────────────

const VisionParamsTab: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(k: K, v: OmniParams[K]) => void;
}> = ({ params, setParam }) => (
  <>
    <LabeledInput label="High Image Mode" info="Tryb wysokiej rozdzielczości — model tnie obraz na 2 plasterki (max_slice=2) dla lepszego rozumienia detali. Wolniej, więcej tokenów.">
      <Toggle
        checked={params.highImage}
        onChange={(v) => {
          setParam('highImage', v);
          setParam('max_slice_nums', v ? 2 : -1);
        }}
        label={params.highImage ? 'High (max_slice=2)' : 'Normal (max_slice=-1)'}
      />
    </LabeledInput>

    <LabeledInput label="Max Slice Nums" info="Ręczna kontrola liczby plasterków obrazu. -1=auto (użyj domyślnego modelu), 1=bez cięcia, 2=wysokiej jakości. Nadpisuje High Image Mode.">
      <input
        type="number"
        value={params.max_slice_nums}
        onChange={(e) => setParam('max_slice_nums', Number(e.target.value))}
        min={-1}
        max={8}
        className={inputCls}
      />
    </LabeledInput>

    <LabeledInput label="Vision Backend" info="Backend przetwarzania wizji. metal=GPU (CUDA/ROCm/Metal), coreml=Apple ANE (tylko macOS). Używaj metal na Linux/Windows.">
      <select
        value={params.vision_backend}
        onChange={(e) => setParam('vision_backend', e.target.value)}
        className={inputCls}
      >
        <option value="metal">metal (GPU)</option>
        <option value="coreml">coreml (Apple ANE)</option>
      </select>
    </LabeledInput>
  </>
);

// ─── Tab: Sliding Window ──────────────────────────────────────────────────────

const SlideWinParamsTab: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(k: K, v: OmniParams[K]) => void;
}> = ({ params, setParam }) => (
  <>
    <LabeledInput label="Mode" info="Strategia zarządzania długim kontekstem. none=brak (do limitu), drop_old=usuwa stare tury gdy przekroczy high_water, summarize=podsumowuje stare tury.">
      <select
        value={params.sliding_window_mode}
        onChange={(e) =>
          setParam(
            'sliding_window_mode',
            e.target.value as OmniParams['sliding_window_mode']
          )
        }
        className={inputCls}
      >
        <option value="none">none — brak zarządzania</option>
        <option value="drop_old">drop_old — usuń stare tury</option>
        <option value="summarize">summarize — podsumuj stare</option>
      </select>
    </LabeledInput>

    <LabeledInput label="High Water (tokens)" info="Próg tokenów przy którym włącza się czyszczenie kontekstu. Gdy KV cache przekroczy tę wartość, usuwa/podsumowuje stare tury.">
      <input
        type="number"
        value={params.sliding_window_high_water}
        onChange={(e) => setParam('sliding_window_high_water', Number(e.target.value))}
        min={256}
        max={32768}
        step={256}
        className={inputCls}
      />
    </LabeledInput>

    <LabeledInput label="Low Water (tokens)" info="Docelowa liczba tokenów po wyczyszczeniu kontekstu. Musi być mniejsza niż High Water. Reszta historii jest usuwana/streszczana.">
      <input
        type="number"
        value={params.sliding_window_low_water}
        onChange={(e) => setParam('sliding_window_low_water', Number(e.target.value))}
        min={128}
        max={16384}
        step={128}
        className={inputCls}
      />
    </LabeledInput>
  </>
);

// ─── Tab: Dirs ────────────────────────────────────────────────────────────────

const DirsParamsTab: React.FC<{
  params: OmniParams;
  setParam: <K extends keyof OmniParams>(k: K, v: OmniParams[K]) => void;
  onApply: () => void;
}> = ({ params, setParam, onApply }) => (
  <>
    <LabeledInput label="Output Directory" info="Katalog serwera gdzie zapisywane są pliki TTS (WAV), flagi generation_done.flag i pliki debug. Musi być dostępny dla procesu llama-server.">
      <input
        type="text"
        value={params.output_dir}
        onChange={(e) => setParam('output_dir', e.target.value)}
        className={inputCls}
      />
    </LabeledInput>

    <button
      onClick={onApply}
      className="w-full py-1.5 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded font-bold mt-1"
    >
      Apply Changes
    </button>
  </>
);

export default LexaOmniV2;
